import React from 'react';
import { ChevronsUpDown, X } from 'lucide-react';
import useDebouncedEffect from '../../hooks/useDebouncedEffect';
import Button from '../Button/Button';
import useDropdownState from '../../hooks/useDropdownState';
import useDropdownEvents from '../../hooks/useDropdownEvents';
import DropdownContent from './DropdownContent';

export interface DropdownItem {
  id: string | number;
  label: string;
  sublabel?: string;
}

interface SearchableDropdownProps {
  items: DropdownItem[];
  selectedItem?: DropdownItem | null;
  onSelect: (item: DropdownItem) => void;
  onSearch: (query: string, page: number) => void;
  onLoadMore?: () => void;
  placeholder?: string;
  searchPlaceholder?: string;
  loading?: boolean;
  hasMore?: boolean;
  showClearButton?: boolean;
  onClear?: () => void;
  className?: string;
  triggerClassName?: string;
  emptyText?: string;
  allowCustomValue?: boolean;
  onCustomValueCreate?: (value: string) => void;
  triggerIcon?: React.ReactNode;
  title?: string;
  floatingMode?: boolean;
  position?: { top: number; left: number };
  onClose?: () => void;
  disabled?: boolean;
}

function SearchableDropdown({
  items,
  selectedItem,
  onSelect,
  onSearch,
  onLoadMore,
  placeholder = 'Select item...',
  searchPlaceholder = 'Search...',
  loading = false,
  hasMore = false,
  showClearButton = false,
  onClear,
  className = '',
  triggerClassName = '',
  emptyText = 'No results found',
  allowCustomValue = false,
  onCustomValueCreate,
  triggerIcon,
  title,
  floatingMode = false,
  position,
  onClose,
  disabled = false
}: Readonly<SearchableDropdownProps>): React.ReactElement | null {
  const {
    isOpen,
    setIsOpen,
    search,
    setSearch,
    page,
    setPage,
    popoverRef,
    triggerRef,
    searchInputRef,
    transitionRef,
    lastCloseTimeRef,
    resetState
  } = useDropdownState();

  const selectedLabel = selectedItem?.label ?? placeholder;

  const shouldShowAddNew =
    allowCustomValue &&
    search.trim() !== '' &&
    !items.some((item) => item.label.toLowerCase() === search.toLowerCase());

  const {
    handleTogglePopover,
    handleListScroll,
    handleSelect,
    handleClear,
    handleCreateCustomValue,
    handleKeyDown
  } = useDropdownEvents({
    isOpen,
    setIsOpen,
    search,
    setSearch,
    page,
    setPage,
    popoverRef,
    triggerRef,
    searchInputRef,
    transitionRef,
    lastCloseTimeRef,
    resetState,
    onSearch,
    onSelect,
    onClose,
    onClear,
    onLoadMore,
    onCustomValueCreate,
    loading,
    hasMore,
    allowCustomValue,
    floatingMode
  });

  useDebouncedEffect(
    () => {
      if (!isOpen) return;
      setPage(0);
      onSearch(search, 0);
    },
    [search, isOpen],
    300
  );

  if (floatingMode && isOpen) {
    return (
      <div
        ref={popoverRef}
        className='absolute bg-white rounded-lg shadow-lg border border-gray-200 z-[9999] overflow-hidden'
        style={{
          top: position ? `${position.top}px` : 0,
          left: position ? `${position.left}px` : 0,
          width: '320px',
          minWidth: '320px'
        }}
        onWheel={(e) => e.stopPropagation()}
      >
        <DropdownContent
          items={items}
          selectedItem={selectedItem}
          search={search}
          setSearch={setSearch}
          searchInputRef={searchInputRef}
          searchPlaceholder={searchPlaceholder}
          loading={loading}
          emptyText={emptyText}
          allowCustomValue={allowCustomValue}
          shouldShowAddNew={shouldShowAddNew}
          handleSelect={handleSelect}
          handleListScroll={handleListScroll}
          handleCreateCustomValue={handleCreateCustomValue}
          handleKeyDown={handleKeyDown}
          hasMore={hasMore}
        />
      </div>
    );
  }

  if (floatingMode) {
    return null;
  }

  return (
    <div
      className={`relative ${className}`}
      onWheel={(e) => isOpen && e.stopPropagation()}
    >
      <div ref={triggerRef}>
        <Button
          type='button'
          variant='outline'
          onClick={handleTogglePopover}
          className={`w-full ${triggerIcon ? 'justify-center' : 'justify-between'} bg-white text-black border border-gray-300 rounded-md ${triggerClassName}`}
          title={title}
          disabled={disabled}
        >
          {triggerIcon ? (
            <span className='flex items-center justify-center'>
              {triggerIcon}
            </span>
          ) : (
            <>
              <span className='truncate'>{selectedLabel}</span>
              <div className='flex items-center'>
                {showClearButton && selectedItem && (
                  <button
                    type='button'
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClear();
                    }}
                    className='mr-1 p-1 text-gray-400 hover:text-gray-600 flex-shrink-0'
                    aria-label='Clear selection'
                    title='Clear selection'
                  >
                    <X size={16} />
                  </button>
                )}
                <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
              </div>
            </>
          )}
        </Button>
      </div>

      {isOpen && (
        <div
          ref={popoverRef}
          className='absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-[9999] overflow-hidden'
          style={{ width: '100%', minWidth: '320px' }}
          onWheel={(e) => e.stopPropagation()}
        >
          <DropdownContent
            items={items}
            selectedItem={selectedItem}
            search={search}
            setSearch={setSearch}
            searchInputRef={searchInputRef}
            searchPlaceholder={searchPlaceholder}
            loading={loading}
            emptyText={emptyText}
            allowCustomValue={allowCustomValue}
            shouldShowAddNew={shouldShowAddNew}
            handleSelect={handleSelect}
            handleListScroll={handleListScroll}
            handleCreateCustomValue={handleCreateCustomValue}
            handleKeyDown={handleKeyDown}
            hasMore={hasMore}
          />
        </div>
      )}
    </div>
  );
}

export default SearchableDropdown;
